<template>
  <div class="workspace-dashboard-page" :style="heroStyle">
    <section class="dashboard-hero">
      <div class="hero-mesh" aria-hidden="true" />
      <div class="hero-glow" aria-hidden="true" />
      <div class="hero-grid" :class="{ 'hero-grid--no-brand': !workspacePhotoUrl }">
        <div class="hero-copy">
          <p class="hero-eyebrow">
            <span class="hero-eyebrow-dot" aria-hidden="true" />
            {{ t('workspaceDashboard.eyebrow') }}
          </p>
          <h1 class="hero-title">
            <span>{{ workspaceName }}</span>
          </h1>
          <p class="hero-subtitle">
            {{ t('workspaceDashboard.subtitle') }}
          </p>
          <div class="hero-chips">
            <span class="hero-chip">{{ isServicos ? t('workspaceDashboard.workspaceType.servicos') : (isCrm ? t('workspaceDashboard.workspaceType.crm') : t('workspaceDashboard.workspaceType.tasks')) }}</span>
            <span class="hero-chip">{{ t('workspaceDashboard.chipSecurity') }}</span>
            <span class="hero-chip hero-chip--accent">{{ t('workspaceDashboard.chipRealtime') }}</span>
          </div>
        </div>
        <div v-if="workspacePhotoUrl" class="hero-brand">
          <div class="hero-workspace-wrap">
            <img
              :src="workspacePhotoUrl"
              :alt="workspaceName"
              class="hero-workspace-photo"
            />
          </div>
        </div>
      </div>

      <div class="hero-metrics" :class="{ 'hero-metrics--crm': isCrm, 'hero-metrics--servicos': isServicos }">
      <WorkspaceDashboardServicosMetrics
        v-if="isServicos && workspaceId"
        :workspace-id="workspaceId"
        :loading="servicosLoading"
        :clientes="servicosDashboard.clientes"
        :financeiro="servicosDashboard.financeiro"
        :agenda="servicosDashboard.agenda"
      />
      <template v-else>
      <div class="metric-card">
        <div class="metric-icon">📦</div>
        <div class="metric-body">
          <p class="metric-label">{{ t('workspaceDashboard.metrics.backlog') }}</p>
          <p class="metric-value">{{ metrics.backlog }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">🧭</div>
        <div class="metric-body">
          <p class="metric-label">{{ t('workspaceDashboard.metrics.active') }}</p>
          <p class="metric-value">{{ metrics.ativos }}</p>
        </div>
      </div>
      <div v-if="!isCrm" class="metric-card">
        <div class="metric-icon">⚠️</div>
        <div class="metric-body">
          <p class="metric-label">{{ t('workspaceDashboard.metrics.overdue') }}</p>
          <p class="metric-value">{{ metrics.atrasados }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">👥</div>
        <div class="metric-body">
          <p class="metric-label">{{ t('workspaceDashboard.metrics.activeUsers') }}</p>
          <p class="metric-value">{{ metrics.usuariosAtivos }}</p>
        </div>
      </div>
      </template>
      </div>
    </section>

    <WorkspaceDashboardServicosSections
      v-if="isServicos && workspaceId"
      :workspace-id="workspaceId"
      :loading="servicosLoading"
      :agenda="servicosDashboard.agenda"
      :forecast="servicosForecast"
    />

    <div v-else class="dashboard-sections">
      <div class="section-card">
        <div class="section-header">
          <h2 class="section-title">{{ t('workspaceDashboard.tags.title') }}</h2>
          <span class="section-pill">{{ t('workspaceDashboard.tags.summary') }}</span>
        </div>
        <div class="tags-summary">
          <div v-if="tagSummary.length === 0" class="empty-state">
            {{ t('workspaceDashboard.tags.empty') }}
          </div>
          <div v-else v-for="categoria in tagSummary" :key="categoria.nome" class="tag-category">
            <div class="tag-category-header">
              <span class="tag-category-name">{{ categoria.nome }}</span>
              <span class="tag-category-count">{{ categoria.total }}</span>
            </div>
            <div class="tag-list">
              <div v-for="tag in categoria.tags" :key="tag.id" class="tag-item">
                <span class="tag-dot" :style="{ background: tag.cor || '#6366f1' }"></span>
                <span class="tag-name">{{ tag.nome }}</span>
                <span class="tag-count">{{ tag.total }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-header">
          <h2 class="section-title">{{ isCrm ? t('workspaceDashboard.pipeline.title') : t('workspaceDashboard.checklist.title') }}</h2>
          <span class="section-pill">{{ isCrm ? t('workspaceDashboard.workspaceType.crm') : t('workspaceDashboard.workspaceType.tasks') }}</span>
        </div>
        <div v-if="!isCrm" class="checklist-summary">
          <div class="checklist-totals-card">
            <div class="checklist-total-item">
              <span>{{ t('workspaceDashboard.checklist.total') }}</span>
              <strong>{{ checklistSummary.total }}</strong>
            </div>
            <div class="checklist-total-item">
              <span>{{ t('workspaceDashboard.checklist.completed') }}</span>
              <strong>{{ checklistSummary.concluidos }}</strong>
            </div>
            <div class="checklist-total-item">
              <span>{{ t('workspaceDashboard.checklist.open') }}</span>
              <strong>{{ checklistSummary.abertos }}</strong>
            </div>
            <div class="checklist-total-item highlight">
              <span>{{ t('workspaceDashboard.checklist.completedLabel') }}</span>
              <strong>{{ checklistSummary.percentual }}%</strong>
            </div>
          </div>
          <div class="progress-card">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: `${checklistSummary.percentual}%` }"></div>
            </div>
            <div class="progress-meta">
              <span class="progress-label">{{ t('workspaceDashboard.checklist.completedLabel') }}</span>
              <span class="progress-value">{{ checklistSummary.percentual }}%</span>
            </div>
          </div>
          <div class="checklist-by-task">
            <div class="checklist-by-task-header">
              <span>{{ t('workspaceDashboard.checklist.byTask') }}</span>
              <span>{{ t('workspaceDashboard.checklist.tasksCount', { count: checklistByTask.length }) }}</span>
            </div>
            <div v-if="checklistByTask.length === 0" class="empty-state">
              {{ t('workspaceDashboard.checklist.empty') }}
            </div>
            <div v-else class="checklist-task-list">
              <div v-for="task in checklistByTask" :key="task.id" class="checklist-task-item">
                <div class="checklist-task-info">
                  <span class="checklist-task-title">{{ task.titulo }}</span>
                  <span class="checklist-task-meta">{{ t('workspaceDashboard.checklist.taskMeta', { completed: task.concluidos, total: task.total }) }}</span>
                </div>
                <div class="checklist-task-progress">
                  <span class="checklist-task-percent">{{ task.percentual }}%</span>
                </div>
                <div class="checklist-task-track">
                  <div class="checklist-task-fill" :style="{ width: `${task.percentual}%` }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="pipeline-summary">
          <div v-if="pipelineSummary.length === 0" class="empty-state">
            {{ t('workspaceDashboard.pipeline.empty') }}
          </div>
          <div v-else class="pipeline-grid">
            <div v-for="coluna in pipelineSummary" :key="coluna.id" class="pipeline-card">
              <div class="pipeline-title">{{ coluna.nome }}</div>
              <div class="pipeline-metric">
                <span>{{ t('workspaceDashboard.pipeline.total') }}</span>
                <span class="pipeline-value">{{ coluna.total }}</span>
              </div>
              <div class="pipeline-metric">
                <span>{{ t('workspaceDashboard.pipeline.potential') }}</span>
                <MoneyAmount :value="coluna.valorPotencial" class="pipeline-value pipeline-value-money" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ações rápidas removidas conforme solicitado -->
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGlobalWorkspaces } from '../composables/useGlobalWorkspaces'
import api from '../services/api'
import { isServicosWorkspace } from '../utils/workspaceTipo'
import MoneyAmount from '../components/MoneyAmount.vue'
import WorkspaceDashboardServicosMetrics from '@/components/workspace-dashboard/WorkspaceDashboardServicosMetrics.vue'
import WorkspaceDashboardServicosSections from '@/components/workspace-dashboard/WorkspaceDashboardServicosSections.vue'

const route = useRoute()
const { t, locale } = useI18n()
const { currentWorkspace, workspaces } = useGlobalWorkspaces()

const workspaceId = computed(() => {
  const id = Number(route.params.id)
  return Number.isNaN(id) ? null : id
})

const workspaceInfo = computed(() => {
  if (currentWorkspace?.value?.id === workspaceId.value) {
    return currentWorkspace.value
  }
  return workspaces.value.find(w => w.id === workspaceId.value) || currentWorkspace.value
})

const workspaceName = computed(() => {
  return workspaceInfo.value?.nome || t('workspaceDashboard.defaultName')
})

const isCrm = computed(() => {
  return String(workspaceInfo.value?.tipo || '').toLowerCase().includes('crm')
})

const isServicos = computed(() => isServicosWorkspace(workspaceInfo.value))

const servicosLoading = ref(false)

const emptyForecast = () => ({
  mesAtual: {
    atrasados: { valor: 0, qtd: 0 },
    recebidos: { valor: 0, qtd: 0 },
    previsaoEntrada: { valor: 0, qtd: 0 },
    saldoLiquido: { valor: 0, qtd: 0 },
    contasPendentesMes: { valor: 0, qtd: 0 }
  },
  proximoMes: { previsaoRecebiveis: { valor: 0, qtd: 0 } }
})

const servicosDashboard = ref({
  clientes: { total: 0, novosNoMes: 0 },
  financeiro: { kpis: {}, forecast: { mesAtual: {}, proximoMes: {} } },
  agenda: { hoje: 0, amanha: 0, proximos7: 0, itensHoje: [], itensAmanha: [] }
})

const servicosForecast = computed(() => {
  const f = servicosDashboard.value.financeiro?.forecast
  if (!f?.mesAtual) return emptyForecast()
  return {
    mesAtual: {
      atrasados: mapBlock(f.mesAtual.atrasados),
      recebidos: mapBlock(f.mesAtual.recebidos),
      previsaoEntrada: mapBlock(f.mesAtual.previsaoEntrada),
      saldoLiquido: mapBlock(f.mesAtual.saldoLiquido),
      contasPendentesMes: mapBlock(f.mesAtual.contasPendentesMes)
    },
    proximoMes: { previsaoRecebiveis: mapBlock(f.proximoMes?.previsaoRecebiveis) }
  }
})

const mapBlock = (b) => ({ valor: Number(b?.valor) || 0, qtd: b?.qtd ?? 0 })

const workspacePhotoUrl = computed(() => {
  const url = workspaceInfo.value?.fotoWorkspace
  return url && String(url).trim() !== '' ? url : null
})

const heroStyle = computed(() => {
  const color = workspaceInfo.value?.corWorkspace || '#6366f1'
  return {
    '--hero-accent': color
  }
})

const tasks = ref([])
const leads = ref([])
const columns = ref([])
const tags = ref([])
const usuariosAcesso = ref([])

const metrics = ref({
  backlog: '—',
  ativos: '—',
  atrasados: '—',
  usuariosAtivos: '—'
})

const tagSummary = ref([])
const checklistSummary = ref({
  total: 0,
  concluidos: 0,
  abertos: 0,
  percentual: 0
})

const checklistByTask = ref([])

const pipelineSummary = ref([])

const parseJsonArray = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const getBacklogColumnIds = () => {
  return columns.value
    .filter(col => String(col.nome || '').toLowerCase().includes('backlog'))
    .map(col => col.id)
}

const countActiveUsers = () => {
  return usuariosAcesso.value.filter(user => user.ativo && !user.convitePendente).length
}

const getActiveItems = () => {
  return isCrm.value
    ? leads.value.filter(lead => lead.isAtivo !== false)
    : tasks.value.filter(task => task.isAtivo !== false)
}

const updateMetrics = () => {
  const backlogColumns = getBacklogColumnIds()
  const activeItems = getActiveItems()

  const backlogCount = backlogColumns.length
    ? activeItems.filter(item => backlogColumns.includes(item.colunaId)).length
    : 0

  const overdueTasks = tasks.value.filter(task => {
    if (task.isAtivo === false) return false
    if (!task.dataVencimento) return false
    if (task.dataConclusao) return false
    return new Date(task.dataVencimento) < new Date()
  }).length

  metrics.value = {
    backlog: backlogColumns.length ? backlogCount : 0,
    ativos: activeItems.length,
    atrasados: isCrm.value ? 0 : overdueTasks,
    usuariosAtivos: countActiveUsers()
  }
}

const updateTagSummary = () => {
  const items = getActiveItems()
  const tagCounts = {}

  items.forEach((item) => {
    const ids = parseJsonArray(item.tags)
    ids.forEach((id) => {
      tagCounts[id] = (tagCounts[id] || 0) + 1
    })
  })

  const grouped = {}
  tags.value.forEach((tag) => {
    const categoria = tag.categoriaNome || t('workspaceDashboard.tags.uncategorized')
    if (!grouped[categoria]) {
      grouped[categoria] = []
    }
    grouped[categoria].push({
      id: tag.id,
      nome: tag.nome,
      cor: tag.cor,
      total: tagCounts[tag.id] || 0
    })
  })

  tagSummary.value = Object.entries(grouped).map(([nome, tags]) => ({
    nome,
    total: tags.reduce((sum, tag) => sum + tag.total, 0),
    tags: tags.sort((a, b) => b.total - a.total)
  }))
}

const updateChecklistSummary = () => {
  if (isCrm.value) return
  let total = 0
  let concluidos = 0
  const byTask = []

  tasks.value.forEach((task) => {
    const checklist = parseJsonArray(task.checklist)
    checklist.forEach((categoria) => {
      const items = categoria?.items || []
      total += items.length
      concluidos += items.filter(item => item.completed).length
    })

    const taskItems = checklist.flatMap(categoria => categoria?.items || [])
    const taskTotal = taskItems.length
    const taskConcluidos = taskItems.filter(item => item.completed).length
    if (taskTotal > 0) {
      byTask.push({
        id: task.id,
        titulo: task.titulo || t('workspaceDashboard.checklist.taskUntitled'),
        total: taskTotal,
        concluidos: taskConcluidos,
        percentual: Math.round((taskConcluidos / taskTotal) * 100)
      })
    }
  })

  const abertos = Math.max(total - concluidos, 0)
  const percentual = total > 0 ? Math.round((concluidos / total) * 100) : 0

  checklistSummary.value = { total, concluidos, abertos, percentual }
  checklistByTask.value = byTask.sort((a, b) => b.percentual - a.percentual)
}

const updatePipelineSummary = () => {
  if (!isCrm.value) return
  const leadsByColumn = {}

  leads.value.forEach((lead) => {
    if (!leadsByColumn[lead.colunaId]) {
      leadsByColumn[lead.colunaId] = []
    }
    leadsByColumn[lead.colunaId].push(lead)
  })

  pipelineSummary.value = columns.value.map((coluna) => {
    const colunaLeads = leadsByColumn[coluna.id] || []
    const valorPotencial = colunaLeads.reduce((sum, lead) => {
      const valor = lead.valorEstimado ? Number(lead.valorEstimado) : 0
      return sum + (Number.isNaN(valor) ? 0 : valor)
    }, 0)
    return {
      id: coluna.id,
      nome: coluna.nome,
      total: colunaLeads.length,
      valorPotencial
    }
  })
}

const updateServicosDashboard = async () => {
  if (!isServicos.value || !workspaceId.value) return
  servicosLoading.value = true
  try {
    const { data } = await api.get(`/clientes/workspace/${workspaceId.value}/dashboard`)
    servicosDashboard.value = {
      clientes: {
        total: data?.clientes?.total ?? 0,
        novosNoMes: data?.clientes?.novosNoMes ?? 0
      },
      financeiro: data?.financeiro || { kpis: {}, forecast: {} },
      agenda: {
        hoje: data?.agenda?.hoje ?? 0,
        amanha: data?.agenda?.amanha ?? 0,
        proximos7: data?.agenda?.proximos7 ?? 0,
        itensHoje: Array.isArray(data?.agenda?.itensHoje) ? data.agenda.itensHoje : [],
        itensAmanha: Array.isArray(data?.agenda?.itensAmanha) ? data.agenda.itensAmanha : []
      }
    }
  } catch (error) {
    console.error('Erro dashboard serviços:', error)
  } finally {
    servicosLoading.value = false
  }
}

const carregarDados = async () => {
  if (!workspaceId.value) return
  if (isServicos.value) {
    await updateServicosDashboard()
    return
  }
  try {
    const [columnsRes, tasksRes, leadsRes, tagsRes, usuariosRes] = await Promise.all([
      api.get(`/colunas-kanban/workspace/${workspaceId.value}`),
      api.get(`/tasks/workspace/${workspaceId.value}`),
      api.get(`/leads/workspace/${workspaceId.value}`),
      api.get(`/workspace-config/${workspaceId.value}/tags`),
      api.get(`/workspace-acesso/workspace/${workspaceId.value}`)
    ])

    columns.value = columnsRes.data || []
    tasks.value = tasksRes.data || []
    leads.value = leadsRes.data || []
    tags.value = tagsRes.data || []
    usuariosAcesso.value = usuariosRes.data || []

    updateMetrics()
    updateTagSummary()
    updateChecklistSummary()
    updatePipelineSummary()
  } catch (error) {
    console.error('Erro ao carregar dashboard do workspace:', error)
  }
}

onMounted(() => {
  carregarDados()
})

watch(workspaceId, () => {
  carregarDados()
})

</script>

<style scoped>
.workspace-dashboard-page {
  padding: 24px;
  max-width: 100%;
  box-sizing: border-box;
  min-height: calc(100vh - 64px);
}

.dashboard-hero {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  padding: 40px 0 36px 40px;
  margin-bottom: 28px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border: 1px solid color-mix(in srgb, var(--hero-accent) 28%, var(--border-primary));
  background:
    radial-gradient(ellipse 90% 70% at 0% 0%, color-mix(in srgb, var(--hero-accent) 18%, transparent), transparent 55%),
    radial-gradient(ellipse 70% 60% at 100% 100%, color-mix(in srgb, #8b5cf6 10%, transparent), transparent 50%),
    linear-gradient(165deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 28px 64px rgba(15, 23, 42, 0.14);
}

.hero-mesh {
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background-image:
    linear-gradient(color-mix(in srgb, var(--border-primary) 35%, transparent) 1px, transparent 1px),
    linear-gradient(90deg, color-mix(in srgb, var(--border-primary) 35%, transparent) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: radial-gradient(ellipse 80% 70% at 50% 40%, black 20%, transparent 75%);
  pointer-events: none;
}

.hero-glow {
  position: absolute;
  top: -40%;
  right: -10%;
  width: 55%;
  height: 120%;
  background: radial-gradient(circle, color-mix(in srgb, var(--hero-accent) 24%, transparent), transparent 65%);
  pointer-events: none;
}

.hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 320px);
  gap: 12px 24px;
  align-items: start;
  flex: 0 0 auto;
  width: 100%;
  padding-right: 12px;
}

.hero-grid--no-brand {
  grid-template-columns: 1fr;
}

.hero-copy { text-align: left; min-width: 0; }

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--hero-accent) 75%, var(--text-secondary));
}

.hero-eyebrow-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--hero-accent), #8b5cf6);
  box-shadow: 0 0 12px color-mix(in srgb, var(--hero-accent) 55%, transparent);
}

.hero-title {
  margin: 0 0 10px;
  font-size: clamp(1.65rem, 2.5vw, 2.15rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
  color: var(--text-primary);
}

.hero-title span {
  color: color-mix(in srgb, var(--hero-accent) 88%, var(--text-primary));
}

.hero-subtitle {
  margin: 0 0 18px;
  max-width: 36rem;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.hero-chips { display: flex; flex-wrap: wrap; gap: 8px; }

.hero-chip {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  background: color-mix(in srgb, var(--bg-primary) 88%, transparent);
  border: 1px solid var(--border-primary);
}

.hero-chip--accent {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, var(--hero-accent), color-mix(in srgb, var(--hero-accent) 70%, #7c3aed));
  box-shadow: 0 4px 14px color-mix(in srgb, var(--hero-accent) 40%, transparent);
}

.hero-brand {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  justify-self: end;
}

.hero-workspace-wrap {
  width: 100%;
  max-width: 280px;
  aspect-ratio: 1;
  max-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-workspace-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 3px solid color-mix(in srgb, var(--hero-accent) 35%, var(--border-primary));
  box-shadow: 0 16px 40px color-mix(in srgb, var(--hero-accent) 35%, transparent);
}

.hero-metrics {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: auto;
  padding-top: 32px;
  padding-right: 40px;
  width: 100%;
  box-sizing: border-box;
}

.hero-metrics--crm { grid-template-columns: repeat(3, minmax(0, 1fr)); }

.hero-metrics--servicos {
  grid-template-columns: 1fr;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--bg-primary) 82%, transparent);
  border: 1px solid var(--border-primary);
  backdrop-filter: blur(8px);
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--hero-accent) 40%, var(--border-primary));
}

.metric-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: color-mix(in srgb, var(--hero-accent) 14%, var(--bg-secondary));
}

.metric-body { min-width: 0; }

.metric-label {
  margin: 0 0 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.metric-value {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}

.dashboard-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.section-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.section-pill {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.16);
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
}

.tags-summary {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tag-category {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 14px;
  background: var(--bg-primary);
}

.tag-category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.tag-category-name {
  font-weight: 600;
  color: var(--text-primary);
}

.tag-category-count {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.tag-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tag-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
}

.tag-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.tag-name {
  color: var(--text-primary);
  font-size: 13px;
  flex: 1;
}

.tag-count {
  font-weight: 600;
  color: var(--text-secondary);
}

.checklist-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checklist-totals-card {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  border: 1px solid color-mix(in srgb, var(--hero-accent) 25%, var(--border-primary));
  border-radius: 4px;
  padding: 14px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
}

.checklist-total-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 10px 12px;
}

.checklist-total-item strong {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.checklist-total-item.highlight strong {
  color: #16a34a;
}

.checklist-by-task {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 12px;
  background: var(--bg-primary);
}

.checklist-by-task-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 10px;
}

.checklist-task-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checklist-task-item {
  border: 1px solid color-mix(in srgb, var(--hero-accent) 20%, var(--border-primary));
  border-radius: 4px;
  padding: 12px 14px;
  display: grid;
  gap: 10px;
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.checklist-task-item:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--hero-accent) 45%, var(--border-primary));
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.14);
}

.checklist-task-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.checklist-task-title {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 13.5px;
}

.checklist-task-meta {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  padding: 4px 8px;
  border-radius: 999px;
}

.checklist-task-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checklist-task-percent {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  background: color-mix(in srgb, var(--hero-accent) 16%, var(--bg-tertiary));
  border: 1px solid color-mix(in srgb, var(--hero-accent) 28%, var(--border-primary));
  padding: 4px 8px;
  border-radius: 999px;
}

.checklist-task-track {
  height: 6px;
  border-radius: 999px;
  background: var(--bg-tertiary);
  overflow: hidden;
  width: 100%;
}

.checklist-task-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--hero-accent), #22c55e);
}

.pipeline-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
}

.pipeline-card {
  border: 1px solid color-mix(in srgb, var(--hero-accent) 20%, var(--border-primary));
  border-radius: 4px;
  padding: 16px;
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.12);
}

.pipeline-title {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 13px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.pipeline-metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: var(--text-secondary);
}

.pipeline-value {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 15px;
}

.pipeline-value-money {
  color: #16a34a;
}

.empty-state {
  color: var(--text-secondary);
  font-size: 14px;
  text-align: center;
  padding: 12px 0;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.activity-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.activity-item.muted .activity-title {
  color: var(--text-secondary);
}

.activity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--hero-accent);
  margin-top: 6px;
}

.activity-title {
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.activity-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 4px 0 0;
}

.progress-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 14px;
  margin-bottom: 16px;
}

.progress-bar {
  height: 8px;
  border-radius: 999px;
  background: var(--bg-tertiary);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #22c55e);
}

.progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 12px;
  color: var(--text-secondary);
}

.insight-card {
  border: 1px dashed var(--border-primary);
  border-radius: 4px;
  padding: 14px;
  background: rgba(99, 102, 241, 0.08);
}

.insight-title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.insight-text {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.quick-actions {
  display: grid;
  gap: 12px;
}

.quick-action {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-primary);
  text-align: left;
  transition: all 0.2s ease;
}

.quick-action:hover {
  border-color: color-mix(in srgb, var(--hero-accent) 40%, transparent);
  box-shadow: 0 10px 20px color-mix(in srgb, var(--hero-accent) 16%, transparent);
  transform: translateY(-1px);
}

.quick-action span {
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--hero-accent) 18%, transparent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.quick-action p {
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.quick-action small {
  font-size: 12px;
  color: var(--text-secondary);
}

@media (max-width: 1024px) {
  .dashboard-hero { min-height: 420px; }
  .hero-grid { grid-template-columns: 1fr; }
  .hero-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hero-metrics--crm { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 768px) {
  .workspace-dashboard-page {
    padding: 10px;
  }

  .dashboard-hero {
    min-height: 0;
    padding: 24px 18px 20px;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    padding-right: 0;
  }

  .hero-brand {
    justify-content: center;
    margin-top: 8px;
  }

  .hero-workspace-wrap {
    max-width: 200px;
    max-height: 200px;
  }

  .hero-metrics {
    grid-template-columns: 1fr;
    gap: 10px;
    padding-top: 20px;
    padding-right: 0;
  }

  .metric-card {
    padding: 12px;
    gap: 10px;
  }

  .dashboard-sections {
    gap: 12px;
  }

  .section-card {
    padding: 12px;
    border-radius: 4px;
  }
}
</style>
